import React, { FC, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Download from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';

import CertificatesForNewAssetsSection from './CertificatesForNewAssetsSection';
import SpecifxInfoForExistingAssetsSection from './SpecifxInfoForExistingAssetsSection';
import IssueReportModal from '../../../sharedModules/issueReports/components/IssueReportModal';

import { CertificateSubmission } from '../../model';
import { AhriCertificate } from '../../model';

interface CertificateSubmissionFullDetailProps {
  certificateSubmission?: CertificateSubmission;
}

const CertificateSubmissionFullDetail: FC<CertificateSubmissionFullDetailProps> = (props) => {
  const { certificateSubmission } = props;

  let [certificateToReport, setCertificateToReport] = useState<AhriCertificate>();
  let [reviewDialogOpen, setReviewDialogOpen] = useState<boolean>(false);
  let [isDownloading, setIsDownloading] = useState<boolean>(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const extractSpecifxData = (specifxInfo: any) => {
    if (!specifxInfo || typeof specifxInfo !== 'object') return {};

    const extracted: any = {};

    // Handle nested results structure
    const results = specifxInfo.results || [specifxInfo];
    const firstResult = results[0] || {};

    // Extract fields
    const fields = firstResult.fields || [];
    fields.forEach((field: any) => {
      if (field.fieldLabel && field.fieldValue) {
        extracted[`Specifx ${field.fieldLabel}`] = field.fieldValue;
      }
    });

    // Extract manufacturer, model, serial from Specifx
    if (firstResult.manufacturer?.fieldValue) {
      extracted['Specifx Manufacturer'] = firstResult.manufacturer.fieldValue;
    }
    if (firstResult.modelNumber?.fieldValue) {
      extracted['Specifx Model Number'] = firstResult.modelNumber.fieldValue;
    }
    if (firstResult.serialNumber?.fieldValue) {
      extracted['Specifx Serial Number'] = firstResult.serialNumber.fieldValue;
    }

    return extracted;
  };

  const extractAhriData = (ahriInfo: any) => {
    if (!ahriInfo || typeof ahriInfo !== 'object') return {};

    const extracted: any = {};

    // Flatten AHRI data
    Object.keys(ahriInfo).forEach((key) => {
      if (ahriInfo[key] && typeof ahriInfo[key] === 'object') {
        // Handle nested objects
        Object.keys(ahriInfo[key]).forEach((nestedKey) => {
          extracted[`AHRI ${key} ${nestedKey}`] = ahriInfo[key][nestedKey];
        });
      } else {
        extracted[`AHRI ${key}`] = ahriInfo[key];
      }
    });

    return extracted;
  };

  const generateCSVData = () => {
    const csvData: any[] = [];

    if (!certificateSubmission) return csvData;

    // Add AHRI certificates to CSV
    certificateSubmission.ahri_certificates?.forEach((certificate) => {
      const specifxData = extractSpecifxData(certificate.specifx_info);
      const ahriData = extractAhriData(certificate.ahri_info);

      csvData.push({
        'Submission Name': certificateSubmission.submission_name,
        'Company Name': certificateSubmission.customer?.name,
        Type: 'New Asset',
        Manufacturer: certificate.manufacturer_name,
        'Model Number': certificate.model_number,
        'Created Date': formatDate(certificateSubmission.created_at),
        ...specifxData,
        ...ahriData,
      });
    });

    // Add assets to CSV
    certificateSubmission.assets?.forEach((asset) => {
      const specifxData = extractSpecifxData(asset.specifx_info);

      csvData.push({
        'Submission Name': certificateSubmission.submission_name,
        'Company Name': certificateSubmission.customer?.name,
        Type: 'Existing Asset',
        Manufacturer: asset.manufacturer_name,
        'Model Number': asset.model_number,
        'Created Date': formatDate(certificateSubmission.created_at),
        ...specifxData,
      });
    });

    return csvData;
  };

  const getCSVFilename = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    return `certificate-submissions-${timestamp}.csv`;
  };

  const downloadPDFAsBlob = async (
    url: string,
    filename: string,
  ): Promise<{ blob: Blob; filename: string; error?: string } | null> => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf,*/*',
        },
      });

      if (!response.ok) {
        const errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        console.error(`Failed to fetch PDF from ${url}: ${errorMsg}`);
        return { blob: new Blob(), filename, error: errorMsg };
      }

      const contentType = response.headers.get('content-type');
      if (
        contentType &&
        !contentType.includes('application/pdf') &&
        !contentType.includes('application/octet-stream')
      ) {
        const errorMsg = `Invalid content type: ${contentType}`;
        console.warn(`PDF at ${url} has unexpected content type: ${errorMsg}`);
        // Still try to download as it might be a PDF with wrong content-type
      }

      const blob = await response.blob();

      // Validate blob size
      if (blob.size === 0) {
        const errorMsg = 'Empty file received';
        console.error(`PDF from ${url} is empty`);
        return { blob: new Blob(), filename, error: errorMsg };
      }

      return { blob, filename };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error(`Error downloading PDF from ${url}:`, error);
      return { blob: new Blob(), filename, error: errorMsg };
    }
  };

  const generateCSVBlob = () => {
    const csvData = generateCSVData();
    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map((row) =>
        headers
          .map((header) => {
            const value = row[header] || '';
            // Escape quotes and wrap in quotes if contains comma
            const escaped = String(value).replace(/"/g, '""');
            return escaped.includes(',') ? `"${escaped}"` : escaped;
          })
          .join(','),
      ),
    ].join('\n');

    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  };

  const handleDownloadAll = async () => {
    setIsDownloading(true);

    try {
      const zip = new JSZip();

      // Add CSV to zip
      const csvBlob = generateCSVBlob();
      zip.file(getCSVFilename(), csvBlob);

      // Collect all PDF URLs
      const pdfPromises: Promise<{ blob: Blob; filename: string; error?: string } | null>[] = [];
      let totalPDFs = 0;

      certificateSubmission.ahri_certificates?.forEach((certificate, index) => {
        if (certificate.certificate_url && certificate.certificate_url !== 'Not Found') {
          totalPDFs++;
          const filename =
            `${certificateSubmission.submission_name}_${certificate.manufacturer_name}_${certificate.model_number}_${index + 1}.pdf`.replace(
              /[^a-zA-Z0-9._-]/g,
              '_',
            ); // Sanitize filename
          pdfPromises.push(downloadPDFAsBlob(certificate.certificate_url, filename));
        }
      });

      // Download all PDFs with error tracking
      const pdfResults = await Promise.all(pdfPromises);

      // Add PDFs to zip and track errors
      const pdfsFolder = zip.folder('certificates');
      let successfulPDFs = 0;
      let failedPDFs = 0;
      const errors: string[] = [];

      pdfResults.forEach((result) => {
        if (result && pdfsFolder) {
          if (result.error) {
            failedPDFs++;
            errors.push(`${result.filename}: ${result.error}`);
            // Still add the file (empty blob) with error info in filename
            const errorFilename = result.filename.replace('.pdf', '_ERROR.txt');
            pdfsFolder.file(errorFilename, `Failed to download: ${result.error}`);
          } else {
            pdfsFolder.file(result.filename, result.blob);
            successfulPDFs++;
          }
        }
      });

      // Add error summary if there were failures
      if (errors.length > 0) {
        const errorSummary = [
          'Download Summary:',
          `Total PDFs attempted: ${totalPDFs}`,
          `Successful downloads: ${successfulPDFs}`,
          `Failed downloads: ${failedPDFs}`,
          '',
          'Failed PDF Details:',
          ...errors,
        ].join('\n');

        zip.file('download_errors.txt', errorSummary);
      }

      // Generate and download zip
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const timestamp = new Date().toISOString().split('T')[0];
      saveAs(zipBlob, `certificate-submissions-complete-${timestamp}.zip`);

      // Show user feedback
      if (failedPDFs > 0) {
        alert(
          `Download completed with ${failedPDFs} failed PDF(s) out of ${totalPDFs}. Check the download_errors.txt file in the ZIP for details.`,
        );
      } else {
        console.log(`Successfully downloaded ${successfulPDFs} PDFs and CSV in zip file`);
      }
    } catch (error) {
      console.error('Error creating download package:', error);
      alert(
        `Error creating download package: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <IssueReportModal
        reportableId={certificateToReport?.id}
        reportableType="AhriCertificate"
        reviewDialogOpen={reviewDialogOpen}
        setReviewDialogOpen={setReviewDialogOpen}
      />
      <Grid item xs={12} key={certificateSubmission.id}>
        <Card>
          <CardContent sx={{ backgroundColor: '#f8f9fa', padding: 3 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="h6" component="div">
                  {certificateSubmission.customer?.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="h6" component="div">
                  {certificateSubmission.submission_name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="body2" color="text.secondary">
                  Created: {formatDate(certificateSubmission.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Chip
                  label={`${(certificateSubmission.ahri_certificates?.length || 0) + (certificateSubmission.assets?.length || 0)} Item${(certificateSubmission.ahri_certificates?.length || 0) + (certificateSubmission.assets?.length || 0) !== 1 ? 's' : ''}`}
                  color="primary"
                  size="small"
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <CertificatesForNewAssetsSection
                submission={certificateSubmission}
                setReviewDialogOpen={setReviewDialogOpen}
                setCertificateToReport={setCertificateToReport}
              />
            </Grid>
            {certificateSubmission?.customer?.id && (
              <Grid item xs={12}>
                <SpecifxInfoForExistingAssetsSection submission={certificateSubmission} />
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* CSV Download Button */}
      {certificateSubmission && (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <Download />}
              size="large"
              onClick={handleDownloadAll}
              disabled={isDownloading}
            >
              {isDownloading ? 'Preparing Download...' : 'Download All (CSV + PDFs)'}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default CertificateSubmissionFullDetail;
